import { CheckBox } from "@/design-system/Check/Check";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import { DoneCheckbox } from "@/design-system/DoneCheckbox/DoneCheckbox";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import { ItemScore } from "@/design-system/ItemScore/ItemScore";
import { ProgressBar } from "@/design-system/ProgressBar/ProgressBar";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { CoinIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import type {
  GalleryLevelPlan,
  GalleryLevelPlanItem,
  GalleryLevelPlanSet,
} from "@repo/shared/api";
import {
  compact,
  firstLevelPlanGroup,
  firstUnfinishedLevelSet,
  grouped,
  levelPlanCategories,
  levelPlanGroups,
  levelPlanLineup,
  levelPlanProgress,
  openSetsAfterDone,
} from "@repo/shared/gallery";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { sbcSetPath } from "@repo/shared/utils";
import { ChevronRight } from "lucide-react";
import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { NavLink } from "react-router";

import { GradeDiamond } from "./GallerySetCard";
import { GallerySolutionView } from "./GallerySolutionView";

type Column = "score" | "toBuy" | "taxLoss";

/** Collected sets show their score; sets to buy also what buying costs. */
const columnsFor = (group: GalleryLevelPlanSet["group"]): Column[] => [
  "score",
  ...(group === "buy" ? (["toBuy", "taxLoss"] as const) : []),
];

const COLUMN_WIDTH: Record<Column, string> = {
  score: "8rem",
  toBuy: "6rem",
  taxLoss: "7rem",
};
// Fixed, not auto: each row is its own grid, so an auto column would size per row and shift the header.
const DONE_WIDTH = "6.5rem";

const NUMBER = "font-oswald text-base";
// The phone row's small numbers, a step brighter than their words.
const META_NUMBER = "font-oswald text-sm text-neutral-300";

const ColumnValue = ({
  column,
  set,
}: {
  column: Column;
  set: GalleryLevelPlanSet;
}) => {
  if (column === "score")
    return (
      <ItemScore
        size="medium"
        score={set.totalScore}
        className="text-neutral-50"
      />
    );
  if (column === "toBuy") return <span className={NUMBER}>{set.toBuy}</span>;
  return (
    <span className={cn("flex items-center gap-x-1.5", NUMBER)}>
      <CoinIcon size={14} />
      {compact.format(set.taxLoss)}
    </span>
  );
};

/** `small`: the phone row's, a step down and quieter so the score leads. */
const SetName = ({
  set,
  small = false,
}: {
  set: GalleryLevelPlanSet;
  small?: boolean;
}) => (
  <span
    className={cn(
      "min-w-0 flex-1 truncate font-semibold",
      small ? "text-sm" : "text-base",
      set.done
        ? "text-neutral-500"
        : small
          ? "text-foreground-body"
          : "text-neutral-50",
    )}
  >
    {set.name}
  </span>
);

/** Done, keeping its click off the row's fold. */
const DoneCell = ({
  set,
  onToggleDone,
}: {
  set: GalleryLevelPlanSet;
  onToggleDone: (set: GalleryLevelPlanSet) => void;
}) => (
  <span
    className="flex shrink-0 justify-end"
    onClick={(e) => e.stopPropagation()}
  >
    <DoneCheckbox checked={set.done} onChange={() => onToggleDone(set)} />
  </span>
);

/** One plan group's sets as table rows; a row folds open to its lineup. */
const SetTable = ({
  sets,
  columns,
  expanded,
  onToggleOpen,
  onToggleDone,
  renderItems,
}: {
  sets: GalleryLevelPlanSet[];
  columns: Column[];
  expanded: number[];
  onToggleOpen: (setId: number) => void;
  onToggleDone: (set: GalleryLevelPlanSet) => void;
  renderItems: (set: GalleryLevelPlanSet) => ReactNode;
}) => {
  const { t } = useTranslation("gallery");
  // From mobile up, every column lines up across the rows and the header.
  const style = {
    "--set-cols": `1.25rem 2rem minmax(0,1fr) ${columns.map((c) => COLUMN_WIDTH[c]).join(" ")} ${DONE_WIDTH}`,
  } as CSSProperties;
  const rowGrid =
    "grid items-center gap-x-5 px-5 [grid-template-columns:var(--set-cols)]";
  const extra = columns.filter((c) => c !== "score");
  return (
    <div
      style={style}
      // Phones: edge to edge, top and bottom borders only.
      className="-mx-4 overflow-hidden border-y border-neutral-700 bg-neutral-800 mobile:mx-0 mobile:rounded-2xl mobile:border"
    >
      {/* Named columns only where there's more than a score to read. */}
      {extra.length > 0 && (
        <div
          className={`text-small ${cn(
            rowGrid,
            "border-b border-neutral-700 py-3 text-neutral-400 max-mobile:hidden",
          )}`}
        >
          <span className="col-start-3">{t("levels.table.set")}</span>
          {columns.map((column) => (
            <span key={column} className="text-right">
              {t(`levels.table.${column}`)}
            </span>
          ))}
          <span />
        </div>
      )}
      <ul className="divide-y divide-neutral-700">
        {sets.map((set) => {
          const open = expanded.includes(set.setId);
          return (
            <li key={set.setId} className="flex flex-col">
              {/* The whole row folds the set; Done keeps its own click. */}
              <div
                onClick={() => onToggleOpen(set.setId)}
                className="cursor-pointer transition-colors hover:bg-white/[0.03]"
              >
                {/* From mobile up: one table row, lined up with the header. */}
                <div className={cn(rowGrid, "py-3.5 max-mobile:hidden")}>
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={set.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleOpen(set.setId);
                    }}
                    className="flex size-5 items-center justify-center text-neutral-400 transition-colors hover:text-white"
                  >
                    <ChevronRight
                      size={16}
                      className={cn(
                        "transition-transform duration-200",
                        open && "rotate-90",
                      )}
                    />
                  </button>
                  <GradeDiamond
                    grade={set.grade}
                    selected={false}
                    reachable
                    completed={set.done}
                  />
                  <SetName set={set} />
                  {columns.map((column) => (
                    <span
                      key={column}
                      className={cn(
                        "flex justify-end tabular-nums text-neutral-100",
                        set.done && "opacity-50",
                      )}
                    >
                      <ColumnValue column={column} set={set} />
                    </span>
                  ))}
                  <DoneCell set={set} onToggleDone={onToggleDone} />
                </div>
                {/* Phones: a to-do row. Done as its checkbox, the name on its own line, the numbers in one line under it. */}
                <div className="flex items-center gap-x-3 py-3 pl-4 pr-3 mobile:hidden">
                  <label
                    onClick={(e) => e.stopPropagation()}
                    className="-m-2 flex shrink-0 cursor-pointer p-2"
                  >
                    <input
                      type="checkbox"
                      checked={set.done}
                      onChange={() => onToggleDone(set)}
                      aria-label={t("levels.tabs.markDone", { name: set.name })}
                      className="sr-only"
                    />
                    <CheckBox
                      checked={set.done}
                      className={cn(
                        "size-5",
                        !set.done && "border-neutral-500",
                      )}
                    />
                  </label>
                  <GradeDiamond
                    grade={set.grade}
                    selected={false}
                    reachable
                    completed={set.done}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-y-0.5">
                    <SetName set={set} small />
                    <span
                      className={cn(
                        // Fixed columns, so the numbers line up from row to row.
                        "grid grid-cols-[5.25rem_4.25rem_auto] items-center justify-start gap-x-3 text-xs text-neutral-500",
                        set.done && "opacity-50",
                      )}
                    >
                      <ItemScore
                        size="medium"
                        score={set.totalScore}
                        className="text-neutral-50"
                      />
                      {set.toBuy > 0 && (
                        <>
                          <span>
                            <Trans
                              ns="gallery"
                              i18nKey="levels.set.toBuy"
                              values={{ count: set.toBuy }}
                              components={{
                                n: <span className={META_NUMBER} />,
                              }}
                            />
                          </span>
                          <span className="flex items-center gap-x-1">
                            <CoinIcon size={11} />
                            <Trans
                              ns="gallery"
                              i18nKey="levels.set.tax"
                              values={{ tax: compact.format(set.taxLoss) }}
                              components={{
                                n: <span className={META_NUMBER} />,
                              }}
                            />
                          </span>
                        </>
                      )}
                    </span>
                  </span>
                  <ChevronRight
                    size={18}
                    aria-hidden
                    className={cn(
                      "shrink-0 text-neutral-500 transition-transform duration-200",
                      open && "rotate-90",
                    )}
                  />
                </div>
              </div>
              <Collapsible open={open}>
                <div className="flex flex-col gap-y-3 border-t border-neutral-700 bg-black/15 p-4">
                  {/* Only on an open set, where the lineup it questions shows; right, and pulled up toward the score it refers to. */}
                  <span className="-mt-2.5 self-end">
                    <InfoTip
                      width={320}
                      icon={
                        <span className="text-xs underline decoration-dotted underline-offset-2">
                          {t("levels.scoreHelp.link")}
                        </span>
                      }
                      className="text-neutral-500 hover:text-neutral-300"
                    >
                      <p>{t("levels.scoreHelp.intro")}</p>
                      <ol className="mt-1.5 flex list-decimal flex-col gap-y-1 pl-5">
                        <li>{t("levels.scoreHelp.autoComplete")}</li>
                        <li>
                          <Trans
                            ns="gallery"
                            i18nKey="levels.scoreHelp.firstOwners"
                            components={{
                              page: (
                                <NavLink
                                  to="/fut-gallery/update-first-owners"
                                  className="text-blue-400 underline"
                                />
                              ),
                            }}
                          />
                        </li>
                        <li>{t("levels.scoreHelp.inSet")}</li>
                      </ol>
                    </InfoTip>
                  </span>
                  {renderItems(set)}
                </div>
              </Collapsible>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * The level plan's sets to grade in two steps, one box per set; a set loads its
 * lineup when it expands. Marking a set done folds it and opens the group's next unfinished set.
 */
export const GalleryLevelPlanDetail = ({
  plan,
  setItems,
  loadSetItems,
  bought,
  onToggleBought,
  onToggleDone,
  summary,
}: {
  plan: GalleryLevelPlan;
  setItems: Record<number, GalleryLevelPlanItem[] | null>;
  loadSetItems: (setId: number) => void;
  bought: number[];
  onToggleBought: (resourceId: number) => void;
  onToggleDone: (set: GalleryLevelPlanSet) => Promise<void>;
  /** The plan's totals, under the heading. */
  summary: ReactNode;
}) => {
  const { t } = useTranslation("gallery");
  // Both groups keep the plan's gallery order. Done sets keep their place.
  const groups = levelPlanGroups(plan);
  // Only the first unfinished set starts open.
  const [expanded, setExpanded] = useState<number[]>(() => {
    const first = firstUnfinishedLevelSet(plan);
    return first ? [first.setId] : [];
  });
  const [tab, setTab] = useState(() => firstLevelPlanGroup(plan));
  const { doneScore, players } = levelPlanProgress(plan, bought.length);
  const progress = [
    {
      key: "score",
      label: t("levels.results.score"),
      value: `${compact.format(doneScore)} / ${compact.format(plan.totals.score)}`,
      ratio: plan.totals.score ? doneScore / plan.totals.score : 1,
    },
    {
      key: "players",
      label: t("levels.results.players"),
      value: `${grouped.format(players)} / ${grouped.format(plan.totals.players)}`,
      ratio: plan.totals.players ? players / plan.totals.players : 1,
    },
  ];

  useEffect(() => {
    for (const setId of expanded) loadSetItems(setId);
  }, [expanded, loadSetItems]);

  const toggleDone = async (set: GalleryLevelPlanSet) => {
    // Marking done folds the set and opens the next one to do.
    if (!set.done) setExpanded((prev) => openSetsAfterDone(plan, prev, set));
    // The mark reverts on a failed write.
    await onToggleDone(set).catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-y-4">
      {/* Like My Club's "For your club" heading. */}
      <h2 className="font-oswald text-2xl font-semibold uppercase leading-tight tracking-wide">
        {t("levels.summary.title", { level: plan.level })}
      </h2>
      {summary}
      {/* The plan's progress right after the tabs; phones wrap it under them. */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <SegmentedControl
          label={t("levels.tabs.label")}
          track="page"
          value={tab}
          onChange={setTab}
          options={groups.map(({ group, sets, done }) => ({
            value: group,
            label: t(`levels.tabs.${group}`, { done, total: sets.length }),
          }))}
        />
        <div className="flex gap-x-5 max-mobile:w-full">
          {progress.map(({ key, label, value, ratio }) => (
            <div
              key={key}
              className="flex w-40 flex-col gap-1.5 max-mobile:w-auto max-mobile:flex-1"
            >
              <span className="flex items-baseline justify-between gap-x-2 text-xs">
                <span className="text-neutral-400">{label}</span>
                <span className="whitespace-nowrap font-oswald text-sm tabular-nums text-neutral-100">
                  {value}
                </span>
              </span>
              <ProgressBar ratio={ratio} small />
            </div>
          ))}
        </div>
      </div>
      {groups
        .filter(({ group }) => group === tab)
        .map(({ group, sets }) => (
          <div key={group}>
            {sets.length === 0 && (
              <p className="text-body text-neutral-400">
                {t("levels.groups.empty")}
              </p>
            )}
            {/* Sets come in gallery order, one table per category. */}
            <div className="flex flex-col gap-y-5">
              {levelPlanCategories(sets).map((chunk) => (
                <div key={chunk.category} className="flex flex-col gap-y-2">
                  <h3 className="text-small font-semibold text-neutral-400">
                    {chunk.category}
                  </h3>
                  <SetTable
                    sets={chunk.sets}
                    columns={columnsFor(group)}
                    expanded={expanded}
                    onToggleOpen={(setId) =>
                      setExpanded((prev) =>
                        prev.includes(setId)
                          ? prev.filter((id) => id !== setId)
                          : [...prev, setId],
                      )
                    }
                    onToggleDone={(set) => void toggleDone(set)}
                    renderItems={(set) => {
                      const items = setItems[set.setId];
                      return items ? (
                        <GallerySolutionView
                          items={levelPlanLineup(items)}
                          collectedMarks={bought}
                          firstOwnerMarks={[]}
                          onToggleCollected={onToggleBought}
                          footer={(item) => {
                            const sbcSet = items.find(
                              (i) => i.resourceId === item.resourceId,
                            )?.sbcSet;
                            return (
                              sbcSet && (
                                <NavLink
                                  to={sbcSetPath(sbcSet)}
                                  className="mt-2 flex items-center gap-x-0.5 text-sm text-blue-400 underline"
                                >
                                  {t("levels.set.openSbc")}
                                  <ChevronRight className="size-4" />
                                </NavLink>
                              )
                            );
                          }}
                        />
                      ) : items === null ? (
                        <p className="text-sm text-amber-400">
                          {t("levels.set.loadError")}
                        </p>
                      ) : (
                        <Skeleton className="h-48 w-full rounded-xl" />
                      );
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};
